import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UndirectedGraphEngine from "../GraphEngine/UndirectedGraphEngine";
import type { NodeId, Edge } from "../GraphEngine/UndirectedGraphEngine";
import styles from "../Graph.module.css";

type TraversalType = "bfs" | "dfs" | null;

const ALPHABET: NodeId[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const UndirectedGraphWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<NodeId[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [traversal, setTraversal] = useState<TraversalType>(null);
  const [startNode, setStartNode] = useState<NodeId | null>(null);
  const [resetSignal, setResetSignal] = useState(0);
  const [traversalResult, setTraversalResult] = useState<NodeId[]>([]);
  const [highlightedNodes, setHighlightedNodes] = useState<NodeId[]>([]);
  const [edgeFrom, setEdgeFrom] = useState<NodeId | "">("");
  const [edgeTo, setEdgeTo] = useState<NodeId | "">("");
  const [nextNodeIndex, setNextNodeIndex] = useState<number>(0);
  const [showBFSPseudo, setShowBFSPseudo] = useState(false);
  const [showDFSPseudo, setShowDFSPseudo] = useState(false);

  useEffect(() => {
    if (nodes.length === 0) {
      setStartNode(null);
      setEdgeFrom("");
      setEdgeTo("");
      return;
    }

    setStartNode((prev) => (prev && nodes.includes(prev) ? prev : nodes[0]));

    setEdgeFrom((prev) =>
      prev && nodes.includes(prev) ? prev : nodes[0]
    );
    setEdgeTo((prev) =>
      prev && nodes.includes(prev)
        ? prev
        : nodes.length > 1
        ? nodes[1]
        : nodes[0]
    );
  }, [nodes]);

    const handleTraversalResult = useCallback(
    (value: React.SetStateAction<NodeId[]>) => {
        setTraversalResult(value);
    },[]);

  const handleAddNode = () => {
    if (nextNodeIndex >= ALPHABET.length) return;
    const newNode = ALPHABET[nextNodeIndex];

    setNodes((prev) => [...prev, newNode]);
    setNextNodeIndex((prev) => prev + 1);
    setTraversal(null);
    setTraversalResult([]);
    setHighlightedNodes([]);
    setResetSignal((s) => s + 1);
  };

  const handleAddEdge = () => {
    if (!edgeFrom || !edgeTo) return;
    if (edgeFrom === edgeTo) return; 

    const [a, b] = edgeFrom < edgeTo ? [edgeFrom, edgeTo] : [edgeTo, edgeFrom];

    const exists = edges.some(
      (e) =>
        (e.from === a && e.to === b) || (e.from === b && e.to === a)
    );
    if (exists) return;

    setEdges((prev) => [...prev, { from: a, to: b }]);
    setTraversal(null);
    setTraversalResult([]);
    setHighlightedNodes([]);
    setResetSignal((s) => s + 1);
  };

  const handleGenerateGraph = () => {
    const baseCount = 5;
    const baseNodes = ALPHABET.slice(0, baseCount);
    const newEdges: Edge[] = [];
    for (let i = 0; i < baseNodes.length - 1; i++) {
      newEdges.push({ from: baseNodes[i], to: baseNodes[i + 1] });
    }

    const possible: Edge[] = [];
    for (let i = 0; i < baseNodes.length; i++) {
      for (let j = i + 1; j < baseNodes.length; j++) {
        const from = baseNodes[i];
        const to = baseNodes[j];
        if (
          !newEdges.some(
            (e) =>
              (e.from === from && e.to === to) ||
              (e.from === to && e.to === from)
          )
        ) {
          possible.push({ from, to });
        }
      }
    }

    const shuffled = [...possible].sort(() => Math.random() - 0.5);
    const extraCount = 2 + Math.floor(Math.random() * 2); // 2–3 extra edges
    const extraEdges = shuffled.slice(0, extraCount);

    const allEdges = [...newEdges, ...extraEdges];

    setNodes(baseNodes);
    setEdges(allEdges);
    setNextNodeIndex(baseCount);
    setTraversal(null);
    setTraversalResult([]);
    setHighlightedNodes([]);
    setResetSignal((s) => s + 1);
  };

  const handleClearGraph = () => {
    setNodes([]);
    setEdges([]);
    setTraversal(null);
    setTraversalResult([]);
    setHighlightedNodes([]);
    setResetSignal((s) => s + 1);
    setNextNodeIndex(0);
  };

  const runTraversal = (type: TraversalType) => {
    if (!startNode || nodes.length === 0) return;
    setTraversal(null);
    setResetSignal(s => s + 1);
    setHighlightedNodes([]);
    setTraversalResult([]);
    setTimeout(() => {
      setTraversal(type);
    }, 0);
  };

  const handleResetHighlighting = () => {
    setTraversal(null);
    setTraversalResult([]);
    setHighlightedNodes([]);
    setResetSignal((s) => s + 1);
  };

  const adjacencyList = useMemo(() => {
    const adj: Record<NodeId, NodeId[]> = {};
    for (const n of nodes) {
      adj[n] = [];
    }

    for (const e of edges) {
      if (!adj[e.from]) adj[e.from] = [];
      if (!adj[e.to]) adj[e.to] = [];
      if (!adj[e.from].includes(e.to)) adj[e.from].push(e.to);
      if (!adj[e.to].includes(e.from)) adj[e.to].push(e.from);
    }

    return adj;
  }, [nodes, edges]);

  const adjacencyMatrix = useMemo(() => {
    const size = nodes.length;
    const index: Record<NodeId, number> = {};
    nodes.forEach((n, i) => {
      index[n] = i;
    });

    const matrix = Array.from({ length: size }, () =>
      Array.from({ length: size }, () => 0)
    );

    for (const e of edges) {
      const i = index[e.from];
      const j = index[e.to];
      if (i === undefined || j === undefined) continue;
      matrix[i][j] = 1;
      matrix[j][i] = 1;
    }

    return matrix;
  }, [nodes, edges]);

  return (
    <div className={styles.graphPageContainer}>
      <button
        onClick={() => navigate(-1)}className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>Undirected Graph</h1>
      <p className={styles.description}>
        This page lets you build your own <strong>undirected graph</strong>.
        Start by adding nodes labeled A, B, C… and connect them with edges.
        We draw the graph in a <strong>circular layout</strong> so the
        structure stays readable as it grows. Each pair of nodes has at most
        one edge and <strong>self-loops are ignored</strong>, which keeps the
        focus on a clean, simple undirected structure.
      </p>

      <div className={styles.useCase}>
        <h3>Use Cases</h3>
        <p>
          Undirected graphs are used for <strong>social networks, road maps, network 
          topology structures, connected-component detection, cycle detection, and 
          modeling real-world two-way relationships.</strong>
        </p>
      </div>

      <div className={styles.timeComplexity}>
        <h3>Time Complexity</h3>
        <p>
          BFS and DFS both run in <strong>O(V + E)</strong>, where V is the number of 
          nodes and E is the number of edges.
        </p>
      </div>

      <div className={styles.graphControlsRow}>
        <div className={styles.graphControlsGroup}>
          <h3>Build Graph</h3>
            <button onClick={handleAddNode}>Add Node</button>
            <button onClick={handleGenerateGraph}>Generate Graph</button>
            <button onClick={handleClearGraph}>Clear Graph</button>
            
          <p className={styles.controlsNote}>
            The graph starts empty. You can{" "}
            <strong>add nodes one by one</strong> or{" "}
            <strong>generate</strong> a starter graph with 5 nodes (A–E) and
            random edges.
          </p>

          <h4 style={{ marginTop: "0.75rem" }}>Add Edge</h4>
          {nodes.length < 2 ? (
            <p className={styles.emptyState}>
              Add at least two nodes to start creating edges.
            </p>
          ) : (
            <div className={styles.inlineForm}>
              <label>
                From:
                <select
                  value={edgeFrom}
                  onChange={(e) =>
                    setEdgeFrom(e.target.value as NodeId)
                  }
                >
                  {nodes.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                To:
                <select
                  value={edgeTo}
                  onChange={(e) =>
                    setEdgeTo(e.target.value as NodeId)
                  }
                >
                  {nodes.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <button onClick={handleAddEdge}>Add Edge</button>
            </div>
          )}
        </div>

        <div className={styles.graphControlsGroup}>
          <h3>Traversal</h3>
          <div className={styles.pseudocodeContainer}>
            <button
              className={styles.pseudocodeToggle}
              onClick={() => setShowBFSPseudo(prev => !prev)}
            >
              {showBFSPseudo ? "▼ Hide BFS Pseudocode" : "▶ Show BFS Pseudocode"}
            </button>

            {showBFSPseudo && (
              <pre className={styles.pseudocodeBlock}>
          {`BFS(start):
            queue ← [start]
            visited ← {start}

            while queue not empty:
                node ← queue.pop_front()
                visit(node)
                for neighbor in adjacency[node]:
                    if neighbor not in visited:
                        visited.add(neighbor)
                        queue.push(neighbor)`}
              </pre>
            )}

            <button
              className={styles.pseudocodeToggle}
              onClick={() => setShowDFSPseudo(prev => !prev)}
            >
              {showDFSPseudo ? "▼ Hide DFS Pseudocode" : "▶ Show DFS Pseudocode"}
            </button>

            {showDFSPseudo && (
              <pre className={styles.pseudocodeBlock}>
          {`DFS(node):
            visited.add(node)
            visit(node)
            for neighbor in adjacency[node]:
                if neighbor not in visited:
                    DFS(neighbor)`}
              </pre>
            )}
          </div>

          {nodes.length === 0 ? (
            <p className={styles.emptyState}>
              Add nodes or generate a graph to run BFS or DFS.
            </p>
          ) : (
            <>
              <div className={styles.inlineForm}>
                <label>
                  Start Node:
                  <select
                    value={startNode ?? ""}
                    onChange={(e) =>
                      setStartNode(e.target.value as NodeId)
                    }
                  >
                    {nodes.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

                <button onClick={() => runTraversal("bfs")}>Run BFS</button>
                <button onClick={() => runTraversal("dfs")}>Run DFS</button>
                <button onClick={handleResetHighlighting}>Reset Highlighting</button>

              <p className={styles.controlsNote}>
                <strong>Note: Breadth-First Search</strong> and <strong>Depth-First Search</strong> do not follow the visual layout of the graph. 
                They always explore based on the <strong>adjacency list</strong> — the order in 
                which each node’s neighbors are stored.

                <p>
                  <strong>Breadth-First Search</strong> visits neighbors level-by-level in adjacency order. <strong>Depth-First Search</strong> goes as deep as possible by always taking the <strong>first unvisited neighbor</strong> in the adjacency list. 
                </p> 

                <p>
                  Because traversal follows adjacency lists (not positions on the screen), the 
                  results may look unexpected at first — this is normal for graph algorithms.  
                </p>

              </p>

            </>
          )}

          <p className={styles.traversalResult}>
            Traversal result:{" "}
            {traversalResult.length > 0
              ? traversalResult.join(" → ")
              : "No traversal run yet."}
          </p>
        </div>
      </div>

      <div className={styles.graphCanvasCard}>
        <UndirectedGraphEngine
          nodes={nodes}
          edges={edges}
          traversal={traversal}
          startNode={startNode}
          resetSignal={resetSignal}
          highlightedNodes={highlightedNodes}
          setHighlightedNodes={setHighlightedNodes}
          setTraversalResult={handleTraversalResult}
        />
      </div>

      <div className={styles.graphRepRow}>
        <div className={styles.graphRepCard}>
          <h3>Adjacency List</h3>
          {nodes.length === 0 ? (
            <p className={styles.emptyState}>
              No nodes yet. Add nodes or generate a graph to see the adjacency
              list.
            </p>
          ) : (
            <pre className={styles.codeBlock}>
              {nodes.map((n) => {
                const neighbors = adjacencyList[n] ?? [];
                return `${n}: ${
                  neighbors.length ? neighbors.join(", ") : "—"
                }\n`;
              })}
            </pre>
          )}
        </div>

        <div className={styles.graphRepCard}>
          <h3>Adjacency Matrix</h3>
          {nodes.length === 0 ? (
            <p className={styles.emptyState}>
              No nodes yet. The adjacency matrix will appear once the graph
              has nodes and edges.
            </p>
          ) : (
            <table className={styles.matrixTable}>
              <thead>
                <tr>
                  <th></th>
                  {nodes.map((n) => (
                    <th key={n}>{n}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {adjacencyMatrix.map((row, i) => (
                  <tr key={nodes[i]}>
                    <th>{nodes[i]}</th>
                    {row.map((val, j) => (
                      <td key={j}>{val}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default UndirectedGraphWrapper;
